import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, MenuItem, Paper, Select, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { useSnackbar } from 'notistack'
import {
  useBulkSetSegmentMutation, useListBrandsQuery, useListPeopleQuery,
} from '../../redux/growthosBrandsApiSlice'
import { closeFormDialog, openCreateDialog, setFilters } from '../../redux/growthosBrandsSlice'
import { apiConstants, get } from '../../common/constants'
import { filenameFromResponse, saveBlob } from '../freight-profitability/freightPnlUtils'
import { SEGMENTS } from './constants'
import BrandFormDialog from './components/BrandFormDialog'
import BrandTable from './components/BrandTable'
import { GX_INK_500, GX_INK_900, GX_CARD_SX, GX_PAGE_SX } from '../growthos-shared/GrowthOsUi'

// Label ABOVE the control, both uppercase via CSS — matches the real filter bar's layout
// (block label, not a floating MUI label).
function FilterField({ label, children }) {
  return (
    <Box>
      <Typography sx={{ display: 'block', mb: 0.5, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: GX_INK_500 }}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}

/**
 * GrowthOS — Brands. Full parity with the real app's Data->Brands page: one unfiltered
 * GET /brands response, bucketed client-side into "Mapping issues" (unmapped OR has a POC
 * mismatch) and "Mapped to HubSpot" (mapped AND no POC mismatch) — same split the real app's
 * settings.js/page.tsx compute, just ported here. Section layout mirrors the real page too:
 * a plain (non-card) title+description, then separate bordered panels for the filter bar and
 * for each table — not one undifferentiated card wrapping everything. "New Brand" stays as an
 * additive extra: the real app has no create-brand UI at all (brands only ever arrive via
 * connector sync), but nothing else creates brands in this migration yet.
 */
export default function BrandsPage() {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { filters, formDialogOpen, editingBrandId } = useSelector((s) => s.growthosBrands)
  const { data, isFetching, error } = useListBrandsQuery()
  const { data: peopleData } = useListPeopleQuery()
  const [bulkSetSegment, bulkState] = useBulkSetSegmentMutation()
  const [downloading, setDownloading] = useState(false)

  const brands = data?.data || []
  const peopleOptions = peopleData?.data || []

  const filtered = useMemo(() => {
    return brands.filter((b) => {
      if (filters.segment && b.segment !== filters.segment) return false
      if (filters.salesPocId && b.salesPocId !== filters.salesPocId) return false
      // Compare on the date part so the "to" day is inclusive — same rule as the export endpoint.
      const onboardedDay = b.onboardedAt ? String(b.onboardedAt).slice(0, 10) : null
      if (filters.from && (!onboardedDay || onboardedDay < filters.from)) return false
      if (filters.to && (!onboardedDay || onboardedDay > filters.to)) return false
      return true
    })
  }, [brands, filters])

  // Exact port of the real app's bucketing: has_unmapped/hubspotMappingStatus OR any POC
  // mismatch -> "Mapping issues"; fully mapped AND no mismatch -> "Mapped to HubSpot".
  const issues = filtered.filter((b) => b.hubspotMappingStatus === 'UNMAPPED' || (b.pocMismatches?.length || 0) > 0)
  const mapped = filtered.filter((b) => b.hubspotMappingStatus === 'MAPPED' && (b.pocMismatches?.length || 0) === 0)

  const handleBulkSme = async () => {
    if (!filtered.length) return
    if (!window.confirm(`Mark all ${filtered.length} filtered brands as SME?`)) return
    try {
      const res = await bulkSetSegment({ brandIds: filtered.map((b) => b.id), segment: 'SME' }).unwrap()
      enqueueSnackbar(`Updated ${res?.updated ?? filtered.length} brands to SME.`, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Bulk update failed.', { variant: 'error' })
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      const res = await get(`${apiConstants.GROWTHOS_ADMIN_BRANDS}/export`, params, { responseType: 'blob' })
      saveBlob(res.data, filenameFromResponse(res, 'brand_kam_mapping.xlsx'))
    } catch (e) {
      enqueueSnackbar('Download failed. Try again.', { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Brands</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" startIcon={<DownloadOutlinedIcon />} disabled={downloading}
            sx={{ textTransform: 'none' }} onClick={handleDownload}
          >
            {downloading ? 'Downloading…' : 'Download mapping report'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={() => dispatch(openCreateDialog())}>
            New brand
          </Button>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: GX_INK_500, mb: 3, maxWidth: 900 }}>
        Each block under "Deal IDs" is a HubSpot Deal ID found in a specific ProShip source tab.
        A HubSpot deal name confirms an exact numeric match only — it is never a recommendation
        based on the visible brand name. Correcting an unmapped or mismatched ID here persists
        the override. <strong>Sales POC</strong> can be corrected inline; a warning under it
        means the raw source value didn't resolve to a known HubSpot owner. <strong>KAM</strong> comes from
        each merchant's Account Team tab and is edited there. <strong>Download mapping report</strong> exports Brand,
        Merchant Brand Name, Company Name, Segment, HSID, Sales POC and KAM (one row per HSID) for the current filters. <strong>Hide from
        display</strong> and <strong>Excluded</strong> are independent flags for downstream
        reporting to key off — this page only sets them, it doesn't consume them.
      </Typography>

      <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FilterField label="Segment">
            <Select size="small" value={filters.segment} displayEmpty sx={{ minWidth: 160, fontSize: 13 }}
              onChange={(e) => dispatch(setFilters({ segment: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              {SEGMENTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FilterField>
          <FilterField label="Sales POC">
            <Select size="small" value={filters.salesPocId} displayEmpty sx={{ minWidth: 180, fontSize: 13 }}
              onChange={(e) => dispatch(setFilters({ salesPocId: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              {peopleOptions.map((p) => <MenuItem key={p.hubspotOwnerId} value={p.hubspotOwnerId}>{p.fullName}</MenuItem>)}
            </Select>
          </FilterField>
          <FilterField label="Onboarded from">
            <TextField size="small" type="date" InputLabelProps={{ shrink: true }} value={filters.from} sx={{ '& input': { fontSize: 13 } }}
              onChange={(e) => dispatch(setFilters({ from: e.target.value }))}
            />
          </FilterField>
          <FilterField label="Onboarded to">
            <TextField size="small" type="date" InputLabelProps={{ shrink: true }} value={filters.to} sx={{ '& input': { fontSize: 13 } }}
              onChange={(e) => dispatch(setFilters({ to: e.target.value }))}
            />
          </FilterField>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined" size="small" disabled={!filtered.length || bulkState.isLoading}
            onClick={handleBulkSme} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
          >
            Mark all as SME ({filtered.length})
          </Button>
        </Box>
      </Paper>

      {error ? (
        <Typography sx={{ fontSize: 13, color: 'error.main' }}>
          Failed to load brands — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Typography>
      ) : isFetching ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading…</Typography>
      ) : (
        <>
          <BrandTable
            title="Mapping issues"
            description="Brands with a raw source identity that doesn't resolve to a canonical HubSpot record — a Deal ID with no matching HubSpot deal, and/or a Sales POC name with no matching HubSpot owner (shown inline in the Sales POC column below). Correct the Deal ID or reassign the Sales POC right here."
            brands={issues}
            peopleOptions={peopleOptions}
          />
          <BrandTable
            title="Mapped to HubSpot"
            description="Every listed source Deal ID resolves to a canonical HubSpot deal, and the Sales POC (if set) exact-matches a HubSpot owner. You can still correct either when needed."
            brands={mapped}
            peopleOptions={peopleOptions}
          />
          {!issues.length && !mapped.length && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No brands match the current filters.</Typography>
          )}
        </>
      )}

      <BrandFormDialog open={formDialogOpen} editingBrandId={editingBrandId} onClose={() => dispatch(closeFormDialog())} />
    </Box>
  )
}
